import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ICampaignRecipient, ICampaignRequest } from '@dataTypes/marketing.type'
import { Button, DatePicker, InputField } from '@components/common'
import { Form } from 'react-bootstrap'
import {
  AGE_RANGE_OPTIONS,
  CELEBRATION_OPTIONS,
  fullDaysOfWeek,
  RECURRING_INTERVAL_OPTIONS,
  UPLOAD_TIME_OPTIONS,
} from '@constants/Marketing'
import { TIMEZONE_OPTIONS } from '@constants/Common/date.constant'
import { ChangedEventArgs } from '@syncfusion/ej2-react-calendars'
import { format } from 'date-fns'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import { getFullName } from '@utils/fullname.util'
import { getContactStatusList, getLocationList } from '@utils/filter.util'
import { useAppSelector } from '@store/hooks'
import { AppointmentStatus } from '@dataTypes/appointment.type'
import { PATIENT_STATUS } from '@constants/PatientManagement/status'
import { isAgeInRange } from '@utils/marketing.utils'
import { IContact } from '@dataTypes/contact.type'
import {
  contactHasCampaignEmail,
  contactHasCampaignSms,
  filterContactsByCampaignChannel,
} from '@utils/campaign.util'

interface SchedulingProps {
  campaignData: ICampaignRequest
  handleChange: (
    field: string,
    value: string | string[] | ICampaignRecipient[],
  ) => void
  validationErrors: { [key: string]: string }
}

const Scheduling: React.FC<SchedulingProps> = ({
  campaignData,
  handleChange,
  validationErrors,
}) => {
  const _contacts = useAppSelector((state) => state.contacts.data)
  const contacts = useMemo(
    () => _contacts.filter((c) => !!c.patient_id),
    [_contacts],
  )
  const eligibleContacts = useMemo(
    () => filterContactsByCampaignChannel(contacts, campaignData.channel),
    [contacts, campaignData.channel],
  )
  const isEmailChannel = campaignData.channel === 'Email'
  const apptTypes = useAppSelector((state) => state.appointmentType.data)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchNewRecipient, setSearchNewRecipient] = useState<string>('')
  const [searchResults, setSearchResults] = useState<ICampaignRecipient[]>(
    campaignData.campaign_recipient ?? [],
  )
  const [contactList, setContactList] = useState<IContact[]>([])

  const celebrationRef = useRef<DropDownButtonComponent>(null)
  const recallTypeRef = useRef<DropDownButtonComponent>(null)
  const locationRef = useRef<DropDownButtonComponent>(null)
  const patientStatusRef = useRef<DropDownButtonComponent>(null)
  const ageRangeRef = useRef<DropDownButtonComponent>(null)
  const apptStatusRef = useRef<DropDownButtonComponent>(null)
  const contactDropDownRef = useRef<HTMLUListElement>(null)

  const handleContactSelect = (contact: IContact): void => {
    const allRecipients = [
      ...campaignData.campaign_recipient,
      {
        contact_id: contact.contact_id!,
        contact: contact,
        campaign_id: campaignData.campaign_id ?? '',
      },
    ]

    const recipientMap = new Map<string, ICampaignRecipient>()

    allRecipients.forEach((recipient) => {
      const existing = recipientMap.get(recipient.contact_id)

      if (!existing) {
        recipientMap.set(recipient.contact_id, recipient)
      } else if (
        !existing.campaign_recipient_id &&
        recipient.campaign_recipient_id
      ) {
        recipientMap.set(recipient.contact_id, recipient)
      }
    })

    const finalRecipients = Array.from(recipientMap.values())
    handleChange('campaign_recipient', finalRecipients)
    setSearchNewRecipient('')
  }

  const recallTypeItems = useMemo(
    () =>
      apptTypes.map((type) => ({
        text: type.name,
        id: type.name,
      })),
    [apptTypes],
  )

  const locationItems = useMemo(
    () =>
      getLocationList(contacts).map((loc) => ({
        text: loc,
        id: loc,
      })),
    [contacts],
  )

  const patientStatusItems = useMemo(
    () =>
      getContactStatusList(contacts).map((status) => ({
        text: status,
        id: status,
      })),
    [contacts],
  )

  const ageRangeItems = useMemo(
    () =>
      AGE_RANGE_OPTIONS.map((option) => ({
        text: option.label,
        id: option.value,
      })),
    [],
  )

  const apptStatusItems = useMemo(
    () =>
      Object.keys(AppointmentStatus)
        .filter((key) => isNaN(Number(key)))
        .map((status) => ({
          text: status,
          id: status,
        })),
    [],
  )

  const handleChangeFilter = (
    field: keyof ICampaignRequest,
    value: string,
  ): void => {
    const selectedFilters = campaignData[field] as string[]
    if (selectedFilters.includes(value)) {
      handleChange(
        field,
        selectedFilters.filter((f) => f !== value),
      )
    } else {
      handleChange(field, [...selectedFilters, value])
    }
  }

  const handleRemoveFilter = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    field: keyof ICampaignRequest,
  ): void => {
    e.stopPropagation()
    e.preventDefault()
    handleChange(field, [])
  }

  const handleChangeRecurringDays = (day: string): void => {
    const recurringDays = campaignData.recurring_days?.split(',') ?? []
    if (campaignData.recurring_days?.includes(day)) {
      handleChange(
        'recurring_days',
        recurringDays.filter((d) => d !== day).join(','),
      )
    } else {
      handleChange('recurring_days', [...recurringDays, day].join(','))
    }
  }

  const handleRemoveRecipient = (contactId: string): void => {
    handleChange(
      'campaign_recipient',
      campaignData.campaign_recipient.filter((r) => r.contact_id !== contactId),
    )
  }

  const handleClearAllRecipients = (): void => {
    handleChange('campaign_recipient', [])
  }

  const onStartDateChange = (args: ChangedEventArgs): void => {
    handleChange('start_date', format(args.value as Date, 'MM/dd/yyyy'))
  }

  const onEndDateChange = (args: ChangedEventArgs): void => {
    handleChange('end_date', format(args.value as Date, 'MM/dd/yyyy'))
  }

  const handleChangeSendingOption = (option: string): void => {
    handleChange('sending_option', option)
  }

  const onBeforeItemRender = (args: any): void => {
    const checkIcon = document.createElement('span')
    if (
      [
        ...campaignData.celebration_type_filter,
        ...campaignData.recall_type_filter,
        ...campaignData.location_filter,
        ...campaignData.patient_status_filter,
        ...campaignData.age_range_filter,
        ...campaignData.appt_status_filter,
      ].includes(args.item.id)
    ) {
      checkIcon.className = 'mgc_check_fill'
      args.element.classList.add('selected-item')
    } else {
      checkIcon.className = 'mc_dd_placeholder'
    }
    args.element.insertBefore(checkIcon, args.element.firstChild)
  }

  useEffect(() => {
    if (celebrationRef.current) {
      let baseClass = 'add-campaign-modal-ddbc e-caret-hide'
      if (campaignData.celebration_type_filter.length > 0) {
        baseClass += ' acm-dd-active'
      }
      celebrationRef.current.cssClass = baseClass
    }
    const isCelebration = (c: IContact): boolean => {
      if (campaignData.celebration_type_filter.includes('Birthday')) {
        const birthday = c.patient?.date_of_birth
        if (birthday && new Date(birthday).getMonth() === new Date().getMonth())
          return true
      }
      if (campaignData.celebration_type_filter.includes('Anniversary'))
        return true
      if (campaignData.celebration_type_filter.includes('Membership')) {
        const membership = c.date_time_created_at
        if (
          membership &&
          new Date(membership).getMonth() === new Date().getMonth()
        )
          return true
      }
      return false
    }
    const filteredContacts: ICampaignRecipient[] = eligibleContacts
      .filter(
        (c) =>
          !campaignData.campaign_recipient.some(
            (r) => r.contact_id == c.contact_id,
          ) && isCelebration(c),
      )
      .map((c) => ({
        contact_id: c.contact_id!,
        contact: c,
        campaign_id: campaignData.campaign_id ?? '',
      }))
    handleChange('campaign_recipient', [
      ...campaignData.campaign_recipient,
      ...filteredContacts,
    ])
  }, [campaignData.celebration_type_filter, eligibleContacts])

  useEffect(() => {
    if (recallTypeRef.current) {
      let baseClass = 'add-campaign-modal-ddbc e-caret-hide'
      if (campaignData.recall_type_filter.length > 0) {
        baseClass += ' acm-dd-active'
      }
      recallTypeRef.current.cssClass = baseClass
    }
    const filteredContacts: ICampaignRecipient[] = eligibleContacts
      .filter(
        (c) =>
          !campaignData.campaign_recipient.some(
            (r) => r.contact_id == c.contact_id,
          ) &&
          campaignData.recall_type_filter.some((r) =>
            c.patient?.appointments?.some((a) => a.appointmenttype.name === r),
          ),
      )
      .map((c) => ({
        contact_id: c.contact_id!,
        contact: c,
        campaign_id: campaignData.campaign_id ?? '',
      }))
    handleChange('campaign_recipient', [
      ...campaignData.campaign_recipient,
      ...filteredContacts,
    ])
  }, [campaignData.recall_type_filter, eligibleContacts])

  useEffect(() => {
    if (locationRef.current) {
      let baseClass = 'add-campaign-modal-ddbc e-caret-hide'
      if (campaignData.location_filter.length > 0) {
        baseClass += ' acm-dd-active'
      }
      locationRef.current.cssClass = baseClass
    }
    const filteredContacts: ICampaignRecipient[] = eligibleContacts
      .filter(
        (c) =>
          !campaignData.campaign_recipient.some(
            (r) => r.contact_id == c.contact_id,
          ) && campaignData.location_filter.includes(c.city),
      )
      .map((c) => ({
        contact_id: c.contact_id!,
        contact: c,
        campaign_id: campaignData.campaign_id ?? '',
      }))
    handleChange('campaign_recipient', [
      ...campaignData.campaign_recipient,
      ...filteredContacts,
    ])
  }, [campaignData.location_filter, eligibleContacts])

  useEffect(() => {
    if (patientStatusRef.current) {
      let baseClass = 'add-campaign-modal-ddbc e-caret-hide'
      if (campaignData.patient_status_filter.length > 0) {
        baseClass += ' acm-dd-active'
      }
      patientStatusRef.current.cssClass = baseClass
    }
    const filteredContacts: ICampaignRecipient[] = eligibleContacts
      .filter(
        (c) =>
          !campaignData.campaign_recipient.some(
            (r) => r.contact_id == c.contact_id,
          ) &&
          campaignData.patient_status_filter.includes(
            PATIENT_STATUS[c.patient?.patient_status ?? 0],
          ),
      )
      .map((c) => ({
        contact_id: c.contact_id!,
        contact: c,
        campaign_id: campaignData.campaign_id ?? '',
      }))
    handleChange('campaign_recipient', [
      ...campaignData.campaign_recipient,
      ...filteredContacts,
    ])
  }, [campaignData.patient_status_filter, eligibleContacts])

  useEffect(() => {
    if (ageRangeRef.current) {
      let baseClass = 'add-campaign-modal-ddbc e-caret-hide'
      if (campaignData.age_range_filter.length > 0) {
        baseClass += ' acm-dd-active'
      }
      ageRangeRef.current.cssClass = baseClass
    }
    const filteredContacts: ICampaignRecipient[] = eligibleContacts
      .filter(
        (c) =>
          !campaignData.campaign_recipient.some(
            (r) => r.contact_id == c.contact_id,
          ) &&
          campaignData.age_range_filter.some((r) =>
            isAgeInRange(r, c.patient?.date_of_birth),
          ),
      )
      .map((c) => ({
        contact_id: c.contact_id!,
        contact: c,
        campaign_id: campaignData.campaign_id ?? '',
      }))
    handleChange('campaign_recipient', [
      ...campaignData.campaign_recipient,
      ...filteredContacts,
    ])
  }, [campaignData.age_range_filter, eligibleContacts])

  useEffect(() => {
    if (apptStatusRef.current) {
      let baseClass = 'add-campaign-modal-ddbc e-caret-hide'
      if (campaignData.appt_status_filter.length > 0) {
        baseClass += ' acm-dd-active'
      }
      apptStatusRef.current.cssClass = baseClass
    }
  }, [campaignData.appt_status_filter])

  useEffect(() => {
    setSearchResults(
      campaignData.campaign_recipient
        ? campaignData.campaign_recipient.filter((r) =>
            getFullName(r.contact, true)
              .toLowerCase()
              .includes(searchQuery.toLowerCase()),
          )
        : [],
    )
  }, [campaignData.campaign_recipient, searchQuery])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (
        contactDropDownRef.current &&
        !contactDropDownRef.current.contains(e.target as Node)
      ) {
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const filteredContacts =
      eligibleContacts.length > 0 && searchNewRecipient.length > 0
        ? eligibleContacts.filter(
            (c) =>
              !campaignData.campaign_recipient.some(
                (r) => r.contact_id == c.contact_id,
              ) &&
              getFullName(c)
                .toLowerCase()
                .includes(searchNewRecipient.toLowerCase()),
          )
        : []

    setContactList(filteredContacts)
  }, [searchNewRecipient, eligibleContacts, campaignData.campaign_recipient])

  // Drop recipients that cannot receive the selected channel.
  useEffect(() => {
    const isEligible = (r: ICampaignRecipient): boolean =>
      isEmailChannel
        ? contactHasCampaignEmail(r.contact)
        : contactHasCampaignSms(r.contact)

    const valid = campaignData.campaign_recipient.filter(isEligible)
    if (valid.length !== campaignData.campaign_recipient.length) {
      handleChange('campaign_recipient', valid)
    }
  }, [campaignData.channel])

  return (
    <div className="ac-scheduling">
      <div className="ac-item">
        <div className="ac-item-input">
          <span className="ac-input-label">Timezone</span>
          <div>
            <Form.Select
              name="timezone"
              value={campaignData.timezone ?? ''}
              onChange={(e) => handleChange('timezone', e.target.value)}
              className={
                validationErrors.timezone ? 'select-field-destructive' : ''
              }
            >
              <option key={'default-null'} value="" disabled />
              {TIMEZONE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {validationErrors.timezone && (
              <span className="hint-text">{validationErrors.timezone}</span>
            )}
          </div>
        </div>
      </div>
      <div className="ac-item">
        <div className="ac-item-input">
          <span className="ac-input-label">Campaign period start</span>
          <div>
            <DatePicker
              id="start-date-picker"
              format="MM/dd/yyyy"
              value={campaignData.start_date ?? ''}
              onDateChange={onStartDateChange}
              allowEdit={false}
              showClearButton={false}
              className={
                validationErrors.start_date ? 'date-picker-destructive' : ''
              }
            />
            {validationErrors.start_date && (
              <span className="hint-text">{validationErrors.start_date}</span>
            )}
          </div>
        </div>
        <div className="ac-item-input">
          <span className="ac-input-label">Time</span>
          <div>
            <Form.Select
              name="start-time"
              value={campaignData.start_time ?? ''}
              onChange={(e) => handleChange('start_time', e.target.value)}
              className={
                validationErrors.start_time ? 'select-field-destructive' : ''
              }
            >
              <option key={'default-null'} value="" disabled />
              {UPLOAD_TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {validationErrors.start_time && (
              <span className="hint-text">{validationErrors.start_time}</span>
            )}
          </div>
        </div>
      </div>
      <div className="ac-item">
        <div className="ac-item-input">
          <span className="ac-input-label">Ends on</span>
          <div>
            <DatePicker
              id="end-date-picker"
              format="MM/dd/yyyy"
              value={campaignData.end_date ?? ''}
              onDateChange={onEndDateChange}
              allowEdit={false}
              showClearButton={false}
              className={
                validationErrors.end_date ? 'date-picker-destructive' : ''
              }
            />
            {validationErrors.end_date && (
              <span className="hint-text">{validationErrors.end_date}</span>
            )}
          </div>
        </div>
        <div className="ac-item-input">
          <span className="ac-input-label">Time</span>
          <div>
            <Form.Select
              name="end-time"
              value={campaignData.end_time ?? ''}
              onChange={(e) => handleChange('end_time', e.target.value)}
              className={
                validationErrors.end_time ? 'select-field-destructive' : ''
              }
            >
              <option key={'default-null'} value="" disabled />
              {UPLOAD_TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {validationErrors.end_time && (
              <span className="hint-text">{validationErrors.end_time}</span>
            )}
          </div>
        </div>
      </div>
      <div className="ac-item">
        <div className="ac-item-input">
          <span className="ac-input-label">
            Message send option
            <span
              className="mgc_information_fill"
              data-bs-toggle="tooltip"
              title="Select the option to send messages at a specific time or interval."
            />
          </span>
          <div>
            <div className="ac-item-input-options">
              <Button
                size="md"
                className={
                  campaignData.sending_option === 'Immediately'
                    ? 'button-secondary'
                    : 'button-secondary-inactive'
                }
                onClick={() => handleChangeSendingOption('Immediately')}
              >
                Immediately
              </Button>
              <Button
                size="md"
                className={
                  campaignData.sending_option === 'Later'
                    ? 'button-secondary'
                    : 'button-secondary-inactive'
                }
                onClick={() => handleChangeSendingOption('Later')}
              >
                Later
              </Button>
              <Button
                size="md"
                className={
                  campaignData.sending_option === 'Recurring'
                    ? 'button-secondary'
                    : 'button-secondary-inactive'
                }
                onClick={() => handleChangeSendingOption('Recurring')}
              >
                Recurring
              </Button>
            </div>
            {validationErrors.sending_option && (
              <span className="hint-text">
                {validationErrors.sending_option}
              </span>
            )}
          </div>
        </div>
        {campaignData.sending_option === 'Recurring' ? (
          <div className="ac-item-input">
            <span className="ac-input-label">Interval</span>
            <div>
              <Form.Select
                name="interval"
                value={campaignData.recurring_interval ?? ''}
                onChange={(e) =>
                  handleChange('recurring_interval', e.target.value)
                }
                className={
                  validationErrors.recurring_interval
                    ? 'select-field-destructive'
                    : ''
                }
              >
                <option key={'default-null'} value="" disabled />
                {RECURRING_INTERVAL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              {validationErrors.recurring_interval && (
                <span className="hint-text">
                  {validationErrors.recurring_interval}
                </span>
              )}
            </div>
          </div>
        ) : campaignData.sending_option === 'Later' ? (
          <div className="ac-item-group-input">
            <div className="ac-item-input">
              <span className="ac-input-label">Send date</span>
              <div>
                <DatePicker
                  id="schedule-date-picker"
                  format="MM/dd/yyyy"
                  value={campaignData.schedule_date ?? ''}
                  onDateChange={(args) =>
                    handleChange(
                      'schedule_date',
                      format(args.value as Date, 'MM/dd/yyyy'),
                    )
                  }
                  min={new Date()}
                  allowEdit={false}
                  showClearButton={false}
                  className={
                    validationErrors.schedule_date
                      ? 'date-picker-destructive'
                      : ''
                  }
                />
                {validationErrors.schedule_date && (
                  <span className="hint-text">
                    {validationErrors.schedule_date}
                  </span>
                )}
              </div>
            </div>
            <div className="ac-item-input">
              <span className="ac-input-label">Time</span>
              <div>
                <Form.Select
                  name="schedule-time"
                  value={campaignData.schedule_time ?? ''}
                  onChange={(e) =>
                    handleChange('schedule_time', e.target.value)
                  }
                  className={
                    validationErrors.schedule_time
                      ? 'select-field-destructive'
                      : ''
                  }
                >
                  <option key={'default-null'} value="" disabled />
                  {UPLOAD_TIME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                {validationErrors.schedule_time && (
                  <span className="hint-text">
                    {validationErrors.schedule_time}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="ac-item-input" />
        )}
      </div>
      {campaignData.sending_option === 'Recurring' && (
        <div className="ac-item">
          <div className="ac-item-input">
            <span className="ac-input-label">Recurring days</span>
            <div>
              <div className="ac-item-group-input day-buttons">
                {fullDaysOfWeek.map((day) => (
                  <Button
                    key={day}
                    size="sm"
                    className={`button-icon ${
                      campaignData.recurring_days?.includes(day) ? 'active' : ''
                    }`}
                    onClick={() => handleChangeRecurringDays(day)}
                    disabled={
                      !campaignData.recurring_interval ||
                      campaignData.recurring_interval === 'Daily'
                    }
                  >
                    {day[0]}
                  </Button>
                ))}
              </div>
              {validationErrors.recurring_days && (
                <span className="hint-text">
                  {validationErrors.recurring_days}
                </span>
              )}
            </div>
          </div>
          <div className="ac-item-group-input">
            <div className="ac-item-input">
              <span className="ac-input-label">Time</span>
              <div>
                <Form.Select
                  name="recurring-time"
                  value={campaignData.recurring_time ?? ''}
                  onChange={(e) =>
                    handleChange('recurring_time', e.target.value)
                  }
                  className={
                    validationErrors.recurring_time
                      ? 'select-field-destructive'
                      : ''
                  }
                >
                  <option key={'default-null'} value="" disabled />
                  {UPLOAD_TIME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                {validationErrors.recurring_time && (
                  <span className="hint-text">
                    {validationErrors.recurring_time}
                  </span>
                )}
              </div>
            </div>
            <div className="ac-item-input">
              <span className="ac-input-label">Recurring for</span>
              <div>
                <div className="ac-item-group-input ac-rf-input-container">
                  <input
                    className="ac-recurring-for-input"
                    value={campaignData.recurring_duration ?? ''}
                    type="number"
                    max={30}
                    min={1}
                    onChange={(e) => {
                      const value = parseInt(e.target.value)
                      if (value >= 1 && value <= 30) {
                        handleChange('recurring_duration', e.target.value)
                      }
                    }}
                  />
                  <span>
                    {campaignData.recurring_interval === 'Weekly'
                      ? 'week'
                      : 'day'}
                    {campaignData.recurring_interval &&
                      campaignData.recurring_duration &&
                      campaignData.recurring_duration > 1 &&
                      's'}
                  </span>
                </div>
                {validationErrors.recurring_duration && (
                  <span className="hint-text">
                    {validationErrors.recurring_duration}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="ac-item">
        <div className="ac-item-input">
          <span className="ac-input-label">Refine recipient type</span>
          <div className="ac-item-group-input ac-recipient-filters">
            {campaignData.type === 'Celebration' && (
              <DropDownButtonComponent
                beforeItemRender={onBeforeItemRender}
                items={CELEBRATION_OPTIONS}
                ref={celebrationRef}
                select={(e) =>
                  handleChangeFilter('celebration_type_filter', e.item.id)
                }
              >
                {campaignData.celebration_type_filter.length === 0 && (
                  <span className="mgc_add_line" />
                )}
                Celebration type{' '}
                {campaignData.celebration_type_filter.length > 0 &&
                  `(${campaignData.celebration_type_filter.length})`}
                {campaignData.celebration_type_filter.length > 0 && (
                  <span
                    className="mgc_close_line"
                    onMouseDown={(e) =>
                      handleRemoveFilter(e, 'celebration_type_filter')
                    }
                  />
                )}
              </DropDownButtonComponent>
            )}
            {campaignData.type === 'Recall & recare' && (
              <DropDownButtonComponent
                beforeItemRender={onBeforeItemRender}
                items={recallTypeItems}
                ref={recallTypeRef}
                select={(e) =>
                  handleChangeFilter('recall_type_filter', e.item.id)
                }
              >
                {campaignData.recall_type_filter.length === 0 && (
                  <span className="mgc_add_line" />
                )}
                Recall type{' '}
                {campaignData.recall_type_filter.length > 0 &&
                  `(${campaignData.recall_type_filter.length})`}
                {campaignData.recall_type_filter.length > 0 && (
                  <span
                    className="mgc_close_line"
                    onMouseDown={(e) =>
                      handleRemoveFilter(e, 'recall_type_filter')
                    }
                  />
                )}
              </DropDownButtonComponent>
            )}
            <DropDownButtonComponent
              beforeItemRender={onBeforeItemRender}
              items={locationItems}
              ref={locationRef}
              select={(e) => handleChangeFilter('location_filter', e.item.id)}
            >
              {campaignData.location_filter.length === 0 && (
                <span className="mgc_add_line" />
              )}
              Location{' '}
              {campaignData.location_filter.length > 0 &&
                `(${campaignData.location_filter.length})`}
              {campaignData.location_filter.length > 0 && (
                <span
                  className="mgc_close_line"
                  onMouseDown={(e) => handleRemoveFilter(e, 'location_filter')}
                />
              )}
            </DropDownButtonComponent>
            <DropDownButtonComponent
              beforeItemRender={onBeforeItemRender}
              items={patientStatusItems}
              ref={patientStatusRef}
              select={(e) => {
                handleChangeFilter('patient_status_filter', e.item.id)
              }}
            >
              {campaignData.patient_status_filter.length === 0 && (
                <span className="mgc_add_line" />
              )}
              Patient status{' '}
              {campaignData.patient_status_filter.length > 0 &&
                `(${campaignData.patient_status_filter.length})`}
              {campaignData.patient_status_filter.length > 0 && (
                <span
                  className="mgc_close_line"
                  onMouseDown={(e) =>
                    handleRemoveFilter(e, 'patient_status_filter')
                  }
                />
              )}
            </DropDownButtonComponent>
            <DropDownButtonComponent
              beforeItemRender={onBeforeItemRender}
              items={ageRangeItems}
              ref={ageRangeRef}
              select={(e) => handleChangeFilter('age_range_filter', e.item.id)}
            >
              {campaignData.age_range_filter.length === 0 && (
                <span className="mgc_add_line" />
              )}
              Age range{' '}
              {campaignData.age_range_filter.length > 0 &&
                `(${campaignData.age_range_filter.length})`}
              {campaignData.age_range_filter.length > 0 && (
                <span
                  className="mgc_close_line"
                  onMouseDown={(e) => handleRemoveFilter(e, 'age_range_filter')}
                />
              )}
            </DropDownButtonComponent>
            <DropDownButtonComponent
              beforeItemRender={onBeforeItemRender}
              items={apptStatusItems}
              ref={apptStatusRef}
              select={(e) =>
                handleChangeFilter('appt_status_filter', e.item.id)
              }
            >
              {campaignData.appt_status_filter.length === 0 && (
                <span className="mgc_add_line" />
              )}
              Appointment status{' '}
              {campaignData.appt_status_filter.length > 0 &&
                `(${campaignData.appt_status_filter.length})`}
              {campaignData.appt_status_filter.length > 0 && (
                <span
                  className="mgc_close_line"
                  onMouseDown={(e) =>
                    handleRemoveFilter(e, 'appt_status_filter')
                  }
                />
              )}
            </DropDownButtonComponent>{' '}
          </div>
        </div>
      </div>
      <div className="ac-item-input ac-recipient-list">
        <span className="ac-input-label">
          <span className="ac-recipient-list-heading">
            Selected recipients
            {campaignData.campaign_recipient.length > 0 &&
              ` (${campaignData.campaign_recipient.length} selected)`}
            {campaignData.campaign_recipient.length > 0 && (
              <button
                type="button"
                className="ac-clear-recipients-btn"
                onClick={handleClearAllRecipients}
              >
                Clear all
              </button>
            )}
          </span>
          <InputField
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<i className="ti ti-search" />}
          />
        </span>
        {isEmailChannel && (
          <span className="hint-text ac-recipient-channel-hint">
            Note: Only contacts with an email address can be selected.
          </span>
        )}
        {validationErrors.campaign_recipient && (
          <span className="hint-text">
            {validationErrors.campaign_recipient}
          </span>
        )}
        {searchQuery.length > 0 && (
          <span className="search-results-for">
            Search results for: {searchQuery}
            <button
              type="button"
              key={`${searchQuery}-close-button`}
              className="unstyled-button mgc_close_line"
              aria-label="Clear search"
              onMouseDown={() => setSearchQuery('')}
            />
          </span>
        )}
        <div className="tag-input-container search-recipient-container">
          {Array.from(new Set(searchResults)).map((recipient) => (
            <div key={recipient.campaign_recipient_id} className="tag-item">
              {getFullName(recipient.contact, true)}
              <button
                type="button"
                className="unstyled-button tag-item-close ti ti-x"
                aria-label="Remove recipient"
                onClick={() => handleRemoveRecipient(recipient.contact_id)}
              />
            </div>
          ))}
          <InputField
            value={searchNewRecipient}
            onChange={(e) => setSearchNewRecipient(e.target.value)}
            className="tag-input flex-grow-1 border-0 shadow-none w-auto"
          />
          {contactList.length > 0 && (
            <ul className="acgm-contact-list" ref={contactDropDownRef}>
              {contactList.map((contact) => (
                <li
                  key={contact.patient_id}
                  onClick={() => handleContactSelect(contact)}
                  className="contact-dropdown-item"
                >
                  {getFullName(contact, true)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default Scheduling
